import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, PanelComponent, PanelRef } from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';
import { WebhookIdInputComponent } from '../../webhook-id-input/webhook-id-input.component';

import { ConnectorForm, MailGunConfiguration } from './mailgun-connector-dialog.models';

@Component({
  selector: 'blaze-mailgun-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    PanelComponent,
    WebhookIdInputComponent,
  ],
  templateUrl: './mailgun-connector-dialog.component.html',
  styleUrls: ['./mailgun-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class MailgunConnectorDialogComponent
  extends BaseConnectorDialogComponent<MailGunConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  protected readonly webhookBaseUrl = '/mailgun/webhook';

  serviceName = 'MailGun';
  connectorForm: FormGroup<ConnectorForm>;
  isLoading = false;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      signingKey: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      useWebhook: new FormControl(false),
      webhookId: new FormControl(''),
    });

    this.initUseWebhookCleanup(this.connectorForm);
  }

  ngOnInit(): void {
    if (this.connector) {
      this.fillForm(this.getConfiguration(this.connector));
    }
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, signingKey, webhookId } = this.connectorForm.value;

    if (!connectorName || !signingKey) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        signingKey,
        id: webhookId ?? '',
      },
    );

    this.isLoading = true;
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  getConfiguration(connector?: ConnectorConfiguration): MailGunConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const signingKey = String(configuration?.signingKey ?? '');
    const id = String(configuration?.id ?? '');

    return {
      connectorId,
      connectorName,
      signingKey,
      id,
    };
  }

  fillForm(configuration: MailGunConfiguration): void {
    const { connectorId, connectorName, signingKey, id } = configuration;

    const useWebhook = !!id;

    this.connectorId = connectorId;

    this.connectorForm.patchValue(
      {
        connectorName,
        signingKey,
        useWebhook,
        webhookId: id,
      },
      { emitEvent: false },
    );
  }
}
